import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useGetQuestionCategories, useGetInterviewQuestions } from '@/reactQuery/get';

interface ComparisonCategoryChartProps {
  questionId: string;
  questionText?: string;
  filterIdA: string;
  filterIdB: string;
  filterAName: string;
  filterBName: string;
}

interface MergedCategory {
  id: string;
  label: string;
  countA: number;
  countB: number;
  percentageA: number;
  percentageB: number;
}

/**
 * Resolves a comparison question_id (which may be AI-generated text) to the
 * real UUID by matching against the project's interview questions.
 */
function resolveQuestionId(
  questionId: string,
  questionText: string | undefined,
  interviewQuestions: Array<{ id: string; question: string }> | undefined,
): string | null {
  if (!interviewQuestions) return null;

  // If questionId is already a UUID, use it directly
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(questionId)) return questionId;

  // Try to match by question_text containing key phrases from each real question
  if (questionText) {
    const searchText = questionText.toLowerCase();
    for (const q of interviewQuestions) {
      const realText = q.question.toLowerCase();
      // Check if the comparison's short question_text appears in the real question
      if (realText.includes(searchText.substring(0, 30))) {
        return q.id;
      }
    }
    // Fuzzy: score each question by how many significant words from the search text appear
    const searchWords = searchText.split(/\s+/).filter((w) => w.length > 3);
    if (searchWords.length > 0) {
      let bestMatch: { id: string; score: number } | null = null;
      for (const q of interviewQuestions) {
        const realText = q.question.toLowerCase();
        const score = searchWords.filter((w) => realText.includes(w)).length;
        if (score >= 2 && (!bestMatch || score > bestMatch.score)) {
          bestMatch = { id: q.id, score };
        }
      }
      if (bestMatch) return bestMatch.id;
    }
  }

  return null;
}

function ComparisonCategoryChart({
  questionId,
  questionText,
  filterIdA,
  filterIdB,
  filterAName,
  filterBName,
}: ComparisonCategoryChartProps) {
  const { project_id: projectId } = useParams();

  // Fetch the project's questions to resolve AI-generated question IDs to real UUIDs
  const { data: interviewQuestions } = useGetInterviewQuestions(null, projectId);

  const resolvedId = useMemo(
    () => resolveQuestionId(questionId, questionText, interviewQuestions),
    [questionId, questionText, interviewQuestions],
  );

  const { data: catDataA, isPending: isPendingA } = useGetQuestionCategories(
    filterIdA,
    projectId,
    resolvedId || undefined,
  );
  const { data: catDataB, isPending: isPendingB } = useGetQuestionCategories(
    filterIdB,
    projectId,
    resolvedId || undefined,
  );

  // Don't render anything if we can't resolve the question ID
  if (!resolvedId) return null;

  if (isPendingA || isPendingB) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading categories...
      </div>
    );
  }

  const categoriesA = catDataA?.categories || catDataA?.categories_multi || [];
  const categoriesB = catDataB?.categories || catDataB?.categories_multi || [];
  const conversationsA = catDataA?.conversations_num || 0;
  const conversationsB = catDataB?.conversations_num || 0;

  if (categoriesA.length === 0 && categoriesB.length === 0) {
    return null;
  }

  // Merge categories by ID — use A's order as primary, append any B-only categories
  const merged: MergedCategory[] = [];
  const bById = new Map(categoriesB.map((c) => [c.id, c]));
  const seen = new Set<string>();

  for (const catA of categoriesA) {
    const catB = bById.get(catA.id);
    merged.push({
      id: catA.id,
      label: catA.label,
      countA: catA.count || 0,
      countB: catB?.count || 0,
      percentageA: conversationsA > 0 ? ((catA.count || 0) / conversationsA) * 100 : 0,
      percentageB: conversationsB > 0 ? ((catB?.count || 0) / conversationsB) * 100 : 0,
    });
    seen.add(catA.id);
  }

  for (const catB of categoriesB) {
    if (!seen.has(catB.id)) {
      merged.push({
        id: catB.id,
        label: catB.label,
        countA: 0,
        countB: catB.count || 0,
        percentageA: 0,
        percentageB: conversationsB > 0 ? ((catB.count || 0) / conversationsB) * 100 : 0,
      });
    }
  }

  if (merged.length === 0) return null;

  const maxPercentage = Math.max(...merged.map((c) => Math.max(c.percentageA, c.percentageB)), 1);

  return (
    <div className="mt-4 pt-3 border-t border-neutral-100">
      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">
        Categories
      </h4>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-neutral-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-emerald-400" />
          {filterAName} ({conversationsA})
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-pink-300" />
          {filterBName} ({conversationsB})
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        {merged.map((cat) => {
          const widthA = (cat.percentageA / maxPercentage) * 100;
          const widthB = (cat.percentageB / maxPercentage) * 100;

          return (
            <div key={cat.id}>
              <p className="text-xs font-medium text-neutral-700 mb-1">{cat.label}</p>
              <div className="flex flex-col gap-0.5">
                {/* Segment A bar */}
                <div className="flex items-center gap-2 h-5">
                  <div className="flex-1 h-full bg-neutral-50 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded-sm flex items-center px-2"
                      style={{
                        width: `${Math.max(widthA, 2)}%`,
                        minWidth: widthA > 0 ? '2.5rem' : undefined,
                      }}
                    >
                      {widthA > 0 && (
                        <span className="text-[10px] font-medium text-white whitespace-nowrap">
                          {cat.percentageA.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-neutral-400 tabular-nums w-6 text-right">
                    {cat.countA}
                  </span>
                </div>
                {/* Segment B bar */}
                <div className="flex items-center gap-2 h-5">
                  <div className="flex-1 h-full bg-neutral-50 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-pink-300 rounded-sm flex items-center px-2"
                      style={{
                        width: `${Math.max(widthB, 2)}%`,
                        minWidth: widthB > 0 ? '2.5rem' : undefined,
                      }}
                    >
                      {widthB > 0 && (
                        <span className="text-[10px] font-medium text-white whitespace-nowrap">
                          {cat.percentageB.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-neutral-400 tabular-nums w-6 text-right">
                    {cat.countB}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ComparisonCategoryChart;
